import React from 'react';
import { Typography, Paper, Grid, Card, CardContent, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import ChatIcon from '@mui/icons-material/Chat';
import LanguageIcon from '@mui/icons-material/Language';
import BusinessIcon from '@mui/icons-material/Business';
import CodeIcon from '@mui/icons-material/Code';

const AITalkerIntegration = () => {
  const integrationOptions = [
    {
      icon: <PhoneIcon />,
      title: "Phone Systems",
      description: "Integrate AI Talker with your existing phone systems to handle incoming calls, route inquiries, and provide automated responses."
    },
    {
      icon: <ChatIcon />,
      title: "Chat Platforms",
      description: "Connect AI Talker to popular chat platforms and messaging apps to provide instant responses to customer inquiries."
    },
    {
      icon: <LanguageIcon />,
      title: "Website Integration",
      description: "Embed AI Talker directly into your website for interactive FAQs, product recommendations, and customer support."
    },
    {
      icon: <BusinessIcon />,
      title: "CRM Systems",
      description: "Seamlessly integrate AI Talker with your CRM to update customer records, log interactions, and provide personalized experiences."
    }
  ];

  const integrationSteps = [
    "Assessment of current systems and integration needs",
    "Development of a customized integration plan",
    "API configuration and setup",
    "Testing of data flow and functionality",
    "Staff training on integrated systems",
    "Deployment and go-live support",
    "Ongoing monitoring and optimization"
  ];

  return (
    <Paper elevation={0}>
      <Typography variant="h4" gutterBottom>AI Talker Integration</Typography>
      <Typography paragraph>
        Integrating AI Talker into your existing systems and workflows is a crucial step in maximizing its 
        potential and ensuring seamless operation within your business environment. Our flexible 
        integration options allow you to connect AI Talker with a wide range of platforms and tools.
      </Typography>

      <Typography variant="h6" gutterBottom>Integration Options:</Typography>
      <Grid container spacing={3}>
        {integrationOptions.map((option, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Card>
              <CardContent>
                {option.icon}
                <Typography variant="h6" component="div">
                  {option.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h6" style={{ marginTop: '20px' }}>API and Custom Integrations:</Typography>
      <Typography paragraph>
        For businesses with unique requirements, we offer a robust API that allows for custom integrations. 
        Our API provides access to AI Talker's core functionalities, enabling you to:
      </Typography>
      <List>
        <ListItem>
          <ListItemIcon><CodeIcon /></ListItemIcon>
          <ListItemText primary="Incorporate AI Talker into your own applications" />
        </ListItem>
        <ListItem>
          <ListItemIcon><CodeIcon /></ListItemIcon>
          <ListItemText primary="Automate workflows based on AI Talker interactions" />
        </ListItem>
        <ListItem>
          <ListItemIcon><CodeIcon /></ListItemIcon>
          <ListItemText primary="Develop custom interfaces for specific use cases" />
        </ListItem>
      </List>

      <Typography variant="h6" style={{ marginTop: '20px' }}>Integration Process:</Typography>
      <List>
        {integrationSteps.map((step, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              <Typography variant="h6">{index + 1}.</Typography>
            </ListItemIcon>
            <ListItemText primary={step} />
          </ListItem>
        ))}
      </List>

      <Typography variant="h6" style={{ marginTop: '20px' }}>Security and Compliance:</Typography>
      <Typography paragraph>
        All integrations are implemented with a strong focus on security and data protection. We ensure that:
      </Typography>
      <List>
        <ListItem>
          <ListItemIcon><CodeIcon /></ListItemIcon>
          <ListItemText primary="Data transfers are encrypted end-to-end" />
        </ListItem>
        <ListItem>
          <ListItemIcon><CodeIcon /></ListItemIcon>
          <ListItemText primary="Access controls are properly configured" />
        </ListItem>
        <ListItem>
          <ListItemIcon><CodeIcon /></ListItemIcon>
          <ListItemText primary="Integrations comply with relevant data protection regulations" />
        </ListItem>
      </List>

      <Typography paragraph style={{ marginTop: '20px' }}>
        Our team of integration specialists is available to guide you through the entire process, 
        from initial planning to post-deployment support. We work closely with your IT team to 
        ensure a smooth integration that aligns with your business processes and technical requirements.
      </Typography>
      <Typography paragraph>
        Whether you're looking to enhance your customer service platform, streamline your sales processes, 
        or create a unique AI-powered solution, our integration capabilities ensure that AI Talker 
        becomes a seamless part of your technology ecosystem.
      </Typography>
    </Paper>
  );
};

export default AITalkerIntegration;
